import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { DataGrid } from "@mui/x-data-grid";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import useSwalWrapper from "@jumbo/vendors/sweetalert2/hooks";
import { formatKotDate } from "../utils/dateHelpers";
import { axios_tenant } from "../../../../axios";

const NoWorkingTeamOverlay = () => {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
      }}
    >
      <Typography>No working team assigned.</Typography>
    </Box>
  );
};

const assignmentStatusOptions = [
  { value: "", label: "Assignment Type" },
  { value: "primary", label: "Primary" },
  { value: "standard", label: "Standard" },
];

const getMemberFullName = (member) => {
  const fullName = `${member.first_name || ""} ${member.last_name || ""}`.trim();
  return fullName || "User";
};

const getAssignmentType = (member) => {
  if (member.isPrimary) return "primary";
  if (member.isBackup) return "backup";
  return "standard";
};

const WorkingTeam = ({ mode }) => {
  const fullClientData = useSelector((state) => state.kotClient);
  const assignmentsByRole = fullClientData?.assignmentsByRole || {};
  const clientId = useSelector((state) => state.kotClient?.clientId);

  const [nameFilter, setNameFilter] = useState("");
  const [emailFilter, setEmailFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [assignmentTypeFilter, setAssignmentTypeFilter] = useState("");
  const [deletingUserId, setDeletingUserId] = useState(null);
  
  const Swal = useSwalWrapper();

  const teamMembers = useMemo(
    () =>
      Object.entries(assignmentsByRole).flatMap(([role, members], roleIndex) =>
        (members || []).map((member, memberIndex) => ({
          ...member,
          id: `${role}-${member.personid || "user"}-${roleIndex}-${memberIndex}`,
          role,
          fullName: getMemberFullName(member),
          assignmentType: getAssignmentType(member),
        })),
      ),
    [assignmentsByRole],
  );

  const roleOptions = useMemo(() => [...new Set(teamMembers.map((member) => member.role).filter(Boolean))], [teamMembers]);

  const filteredMembers = useMemo(() => {
    return teamMembers.filter((member) => {
      const matchesName = nameFilter === "" || member.fullName.toLowerCase().includes(nameFilter.trim().toLowerCase());
      const matchesEmail = emailFilter === "" || (member.email || "").toLowerCase().includes(emailFilter.trim().toLowerCase());
      const matchesRole = roleFilter === "" || member.role === roleFilter;
      const matchesAssignmentType = assignmentTypeFilter === "" || member.assignmentType === assignmentTypeFilter;

      return matchesName && matchesEmail && matchesRole && matchesAssignmentType;
    });
  }, [assignmentTypeFilter, emailFilter, nameFilter, roleFilter, teamMembers]);

  const handleDeleteAssignment = async (member) => {
    const result = await Swal.fire({
      title: "Remove Team Member?",
      html: `<p>Are you sure you want to remove <strong>${member.fullName}</strong> from the working team?</p>`,
      // icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Remove",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d32f2f",
    });

    if (!result.isConfirmed) return;

    try {
      setDeletingUserId(member.personid);
      await axios_tenant.delete(`/kot/client-profiles/${clientId}/assignments/${member.personid}`);

      toast.success(`${member.fullName} has been removed from the working team`, {
        position: "top-right",
        autoClose: 3000,
      });

      // Refresh the page or refetch data
      globalThis.dispatchEvent(new CustomEvent("refresh-client-profile"));
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Failed to remove team member";
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setDeletingUserId(null);
    }
  };

  const columns = [
    {
      field: "fullName",
      headerName: "Name",
      flex: 0.9,
      minWidth: 180,
      renderCell: (params) => <span>{params.row.fullName}</span>,
    },
    {
      field: "role",
      headerName: "Role",
      flex: 1.1,
      minWidth: 220,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
          <Typography variant="body2" noWrap>
            {params.row.role || "-"}
          </Typography>
          {params.row.isPrimary && (
            <Chip
              label="Primary"
              size="small"
              color="primary"
              sx={{
                height: 20,
                fontSize: "0.7rem",
                backgroundColor: "#e3f2fd",
                color: "#1976d2",
              }}
            />
          )}
          {params.row.isBackup && (
            <Chip
              label="Backup"
              size="small"
              sx={{
                height: 20,
                fontSize: "0.7rem",
                backgroundColor: "#f5f5f5",
              }}
            />
          )}
        </Box>
      ),
    },
    {
      field: "email",
      headerName: "Email Address",
      flex: 1,
      minWidth: 220,
      renderCell: (params) => <span>{params.row.email || "-"}</span>,
    },
    {
      field: "assignedAt",
      headerName: "Created On",
      flex: 0.7,
      minWidth: 140,
      renderCell: (params) => <span>{formatKotDate(params.row.assignedAt)}</span>,
    },
  ];

  return (
    <Box p={{ xs: 2, md: 3 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", xl: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", xl: "flex-start" },
          gap: 2,
          mb: 2,
        }}
      >
        <Typography variant="h3" sx={{ alignSelf: { xs: "flex-start", xl: "center" } }}>
          Working Team
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1.5,
            alignItems: "center",
            alignContent: "center",
            justifyContent: { xs: "stretch", md: "flex-end" },
            "> .MuiFormControl-root, > .MuiTextField-root": {
              minWidth: { xs: "100%", sm: 180 },
              flex: { xs: "1 1 100%", sm: "0 1 180px" },
            },
          }}
        >
          <TextField
            size="small"
            label="Name"
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
          />
          <TextField
            size="small"
            label="Email Address"
            value={emailFilter}
            onChange={(e) => setEmailFilter(e.target.value)}
          />
          <FormControl size="small">
            <InputLabel id="working-team-role-label">Role</InputLabel>
            <Select
              labelId="working-team-role-label"
              value={roleFilter}
              label="Role"
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {roleOptions.map((role) => (
                <MenuItem key={role} value={role}>
                  {role}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel id="working-team-assignment-type-label">Assignment Type</InputLabel>
            <Select
              labelId="working-team-assignment-type-label"
              value={assignmentTypeFilter}
              label="Assignment Type"
              onChange={(e) => setAssignmentTypeFilter(e.target.value)}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {assignmentStatusOptions
                .filter((option) => option.value !== "")
                .map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          {mode === "edit" && fullClientData?.isPrivate && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => document.dispatchEvent(new CustomEvent("open-assignment-modal", { detail: { mode: "assign" } }))}
            >
              Manage Team
            </Button>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          height: { xs: 520, md: 600 },
          width: "100%",
          background: "#fff",
          border: "1px solid #ddd",
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <DataGrid
          rows={filteredMembers}
          columns={columns}
          disableRowSelectionOnClick
          disableColumnFilter
          disableColumnMenu
          initialState={{
            pagination: {
              paginationModel: {
                page: 0,
                pageSize: 100,
              },
            },
          }}
          pageSizeOptions={[25, 50, 100]}
          slots={{
            noRowsOverlay: NoWorkingTeamOverlay,
          }}
          sx={{
            border: 0,
            minWidth: 0,
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#f5f7fb",
              fontWeight: "bold",
              borderBottom: "1px solid #ddd",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "1px solid #eee",
            },
            "& .MuiDataGrid-columnSeparator": {
              visibility: "visible",
            },
            "& .MuiDataGrid-cell, & .MuiDataGrid-columnHeaderTitle": {
              whiteSpace: "normal",
            },
            // Custom pagination styles
            ".MuiTablePagination-displayedRows": {
              marginTop: "1em",
              marginBottom: "1em",
            },
            ".MuiTablePagination-selectLabel": {
              marginTop: "1em",
              marginBottom: "1em",
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default WorkingTeam;
